import React, { useState, useEffect } from 'react';
import { projectAPI } from '../../../api/services';

interface Props {
  selectedProjectId: string | null;
  onSelectProject: (project: any) => void;
}

const statusColors: Record<string, string> = {
  planning: 'bg-gray-400', development: 'bg-blue-500', testing: 'bg-purple-500',
  done: 'bg-green-500', production: 'bg-emerald-500', archived: 'bg-gray-300',
};

const SECTORS = [
  { value: 'technology', label: 'Technology' }, { value: 'sales', label: 'Sales' },
  { value: 'finance', label: 'Finance' }, { value: 'logistics', label: 'Logistics' },
  { value: 'support', label: 'Support' }, { value: 'hr', label: 'HR' },
  { value: 'marketing', label: 'Marketing' }, { value: 'operations', label: 'Operations' },
  { value: 'custom', label: 'Custom' },
];

const ProjectTree: React.FC<Props> = ({ selectedProjectId, onSelectProject }) => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: '', description: '', visibility: 'private',
    status: 'planning', priority: 'medium', sector: 'technology',
    workMode: 'fluid' as 'strict' | 'fluid',
    startDate: '', estimatedEndDate: '', tags: '',
  });

  const fetchProjects = async () => {
    try {
      const res = await projectAPI.getMyProjects();
      const data = res.data.data || res.data;
      setProjects(Array.isArray(data) ? data : []);
    } catch { setProjects([]); }
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => {
    const handler = () => fetchProjects();
    window.addEventListener('project-deleted', handler);
    return () => window.removeEventListener('project-deleted', handler);
  }, []);

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Project name is required';
    else if (form.name.trim().length < 3) e.name = 'Name must be at least 3 characters';
    if (!form.description.trim()) e.description = 'Description is required';
    else if (form.description.trim().length < 10) e.description = 'Description must be at least 10 characters';
    if (!form.startDate) e.startDate = 'Start date is required';
    if (form.estimatedEndDate && form.startDate && new Date(form.estimatedEndDate) <= new Date(form.startDate)) e.estimatedEndDate = 'End date must be after start date';
    return e;
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) { setCreateError(''); return; }
    setCreating(true);
    setCreateError('');
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const res = await projectAPI.createProject({
        name: form.name.trim(),
        description: form.description.trim(),
        visibility: form.visibility,
        status: form.status,
        priority: form.priority,
        sector: form.sector,
        workMode: form.workMode,
        dates: { startDate: form.startDate || undefined, estimatedEndDate: form.estimatedEndDate || undefined },
        tags,
      } as any);
      const newProject = res.data.data ?? res.data;
      setShowCreate(false);
      setFieldErrors({});
      setForm({ name: '', description: '', visibility: 'private', status: 'planning', priority: 'medium', sector: 'technology', workMode: 'fluid', startDate: '', estimatedEndDate: '', tags: '' });
      await fetchProjects();
      if (newProject?._id) onSelectProject(newProject);
    } catch (err: any) {
      const msg = err.response?.data?.message || '';
      if (msg.includes('duplicate') || msg.includes('E11000')) setFieldErrors({ name: 'A project with this name already exists' });
      else setCreateError(msg || 'Failed to create project');
    }
    setCreating(false);
  };

  const filtered = projects
    .filter(p => p.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const inputCls = (field?: string) => `w-full border rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
    field && fieldErrors[field] ? 'border-red-300 focus:ring-red-500/30 focus:border-red-400' : 'border-gray-200 focus:ring-blue-500/30 focus:border-blue-400'
  }`;
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5';
  const errorCls = 'text-xs text-red-500 mt-1 flex items-center gap-1';
  const FieldError = ({ field }: { field: string }) => fieldErrors[field] ? <p className={errorCls}><svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{fieldErrors[field]}</p> : null;
  const clearError = (field: string) => { if (fieldErrors[field]) setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; }); };

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 pb-1">
        <input
          type="text" placeholder="Search..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Section header with + button */}
      <div className="flex items-center justify-between px-3 py-1.5">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Projects</span>
        <button
          onClick={() => setShowCreate(true)}
          className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="New project"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      </div>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-400">No projects yet</p>
            <button onClick={() => setShowCreate(true)} className="text-xs text-blue-600 hover:text-blue-700 mt-1">Create one</button>
          </div>
        ) : (
          filtered.map(project => {
            const isActive = project._id === selectedProjectId;
            return (
              <div
                key={project._id}
                onClick={() => onSelectProject(project)}
                title={`${project.name}\nBy: ${project.owner?.name || 'Unknown'}`}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${statusColors[project.status] || 'bg-gray-400'}`} />
                <span className="truncate flex-1 capitalize">{project.name}</span>
                {project.stats?.totalTasks != null && (
                  <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 shrink-0">{project.stats.totalTasks}</span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create project modal */}
      {showCreate && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center p-4 sm:p-6 md:p-8" style={{ zIndex: 9999 }} onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleCreate} className="flex flex-col flex-1 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">New Project</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Create a new project to organize your work</p>
                </div>
                <button type="button" onClick={() => setShowCreate(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Body — scrollable */}
              <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
                {createError && <div className="bg-red-50 text-red-700 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2"><svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{createError}</div>}

                <div>
                  <label className={labelCls}>Project Name *</label>
                  <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); clearError('name'); }} placeholder="My Project" className={inputCls('name')} />
                  <FieldError field="name" />
                </div>

                <div>
                  <label className={labelCls}>Description *</label>
                  <textarea value={form.description} onChange={e => { setForm(f => ({ ...f, description: e.target.value })); clearError('description'); }} placeholder="What is this project about?" rows={3} className={`${inputCls('description')} resize-none`} />
                  <FieldError field="description" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Sector</label>
                    <select value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} className={inputCls()}>
                      {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Priority</label>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={inputCls()}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Work Mode</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'fluid' as const, l: 'Fluid', d: 'Self-directed, flexible dates' },
                      { v: 'strict' as const, l: 'Strict', d: 'Sprint-governed, fixed dates' },
                    ].map(m => (
                      <label key={m.v} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.workMode === m.v ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" name="workMode" checked={form.workMode === m.v} onChange={() => setForm(f => ({ ...f, workMode: m.v }))} className="mt-0.5" />
                        <div>
                          <div className="text-xs font-medium text-gray-900">{m.l}</div>
                          <div className="text-xs text-gray-500">{m.d}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Start Date *</label>
                    <input type="date" value={form.startDate} onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, startDate: val }));
                      clearError('startDate');
                      // Cross-validate end date
                      if (form.estimatedEndDate && val && new Date(form.estimatedEndDate) <= new Date(val)) {
                        setFieldErrors(prev => ({ ...prev, estimatedEndDate: 'End date must be after start date' }));
                      } else { clearError('estimatedEndDate'); }
                    }} className={inputCls('startDate')} />
                    <FieldError field="startDate" />
                  </div>
                  <div>
                    <label className={labelCls}>End Date</label>
                    <input type="date" value={form.estimatedEndDate} onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, estimatedEndDate: val }));
                      if (val && form.startDate && new Date(val) <= new Date(form.startDate)) {
                        setFieldErrors(prev => ({ ...prev, estimatedEndDate: 'End date must be after start date' }));
                      } else { clearError('estimatedEndDate'); }
                    }} className={inputCls('estimatedEndDate')} />
                    <FieldError field="estimatedEndDate" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Visibility</label>
                    <select value={form.visibility} onChange={e => setForm(f => ({ ...f, visibility: e.target.value }))} className={inputCls()}>
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputCls()}>
                      <option value="planning">Planning</option>
                      <option value="development">Development</option>
                      <option value="testing">Testing</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Tags</label>
                  <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="auth, payments, ui (comma-separated)" className={inputCls()} />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
                <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-800 rounded-xl hover:bg-gray-100 transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-colors">
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTree;
